import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExchangeRateContext = createContext();

export const useExchangeRate = () => {
  const context = useContext(ExchangeRateContext);
  if (!context) {
    throw new Error('useExchangeRate must be used within ExchangeRateProvider');
  }
  return context;
};

export const ExchangeRateProvider = ({ children }) => {
  const [rate, setRate] = useState(1560); // Default fallback
  const [loading, setLoading] = useState(true);

  const fetchExchangeRate = async () => {
    try {
      const response = await axios.get(`${API}/settings/exchange-rate`);
      setRate(response.data.rate || 1560);
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      // Keep default rate on error
      setRate(1560);
    } finally {
      setLoading(false);
    }
  };

  const updateExchangeRate = async (newRate) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/settings/exchange-rate?new_rate=${newRate}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      
      if (response.data.success) {
        setRate(response.data.rate);
        return { success: true, message: response.data.message };
      }
      
      return { success: false, message: 'Failed to update exchange rate' };
    } catch (error) {
      console.error('Error updating exchange rate:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Failed to update exchange rate'
      };
    }
  };

  const convertUSDToNGN = (usdAmount) => {
    return usdAmount * rate;
  };

  const formatNGN = (amount) => {
    return `₦${amount.toLocaleString()}`;
  };

  const formatUSD = (amount) => {
    return `$${amount.toLocaleString()}`;
  };

  useEffect(() => {
    fetchExchangeRate();
    
    // Refresh exchange rate every 5 minutes
    const interval = setInterval(fetchExchangeRate, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const value = {
    rate,
    loading,
    updateExchangeRate,
    refreshRate: fetchExchangeRate,
    convertUSDToNGN,
    formatNGN,
    formatUSD
  };

  return (
    <ExchangeRateContext.Provider value={value}>
      {children}
    </ExchangeRateContext.Provider>
  );
};

export default ExchangeRateContext;
